import { useUpdatePrimaryCard } from '@/api/hooks/useUpdatePrimaryCard'
import Config from '@/config'
import { Button, ButtonText } from '@/ui/core/button'
import { HStack, VStack } from '@/ui/core/stack'
import {
	CardCvcElement,
	CardExpiryElement,
	CardNumberElement,
	Elements,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useMemo, useState } from 'react'
import { View } from 'react-native'
import { useWindowDimensions } from 'react-native'
import Toast from 'react-native-toast-message'

const ELEMENTS_OPTIONS = {
	fonts: [
		{
			cssSrc: 'https://fonts.googleapis.com/css2?family=Lato:wght@400&display=swap',
		},
	],
}

const generateCardNumberStyleOptions = ({ windowWidth }: { windowWidth: number }) => ({
	showIcon: false,
	placeholder: 'Card Number',
	classes: {
		base: 'bg-palette_1 px-3 py-3 rounded-md',
	},
	style: {
		base: {
			fontSize: windowWidth > 992 ? '18px' : '16px',
			fontFamily: 'Lato',
			color: '#000000',
			'::placeholder': {
				color: '#838383',
			},
			fontWeight: '600',
		},
		invalid: {
			color: '#F92E32',
		},
	},
})

const generateCardMetadataOptions = ({ windowWidth }: { windowWidth: number }) => ({
	classes: {
		base: 'bg-palette_1 px-3 py-3 rounded-md w-1/2',
	},
	style: {
		base: {
			fontSize: windowWidth > 992 ? '18px' : '16px',
			fontFamily: 'Lato',
			color: '#000000',
			'::placeholder': {
				color: '#838383',
			},
		},
		invalid: {
			color: '#F92E32',
		},
	},
})

const BillingCardFormInternal = ({ onComplete = () => {} }) => {
	const [isSaving, setIsSaving] = useState(false)
	const [isCardNumberComplete, setIsCardNumberComplete] = useState(false)
	const [isCardExpiryComplete, setIsCardExpiryComplete] = useState(false)
	const [isCardCvcComplete, setIsCardCvcComplete] = useState(false)
	const { width: windowWidth } = useWindowDimensions()
	const { mutate: updatePrimaryCard } = useUpdatePrimaryCard()
	const stripe = useStripe()
	const elements = useElements()

	const isCardComplete = useMemo(
		() => isCardNumberComplete && isCardCvcComplete && isCardExpiryComplete,
		[isCardNumberComplete, isCardExpiryComplete, isCardCvcComplete]
	)

	const onCardNumberChange = ({ complete }: { complete: boolean }) => {
		setIsCardNumberComplete(complete)
	}

	const onCardExpiryChange = ({ complete }: { complete: boolean }) => {
		setIsCardExpiryComplete(complete)
	}

	const onCardCvcChange = ({ complete }: { complete: boolean }) => {
		setIsCardCvcComplete(complete)
	}

	const onCardSave = async () => {
		setIsSaving(true)

		if (!stripe) {
			setIsSaving(false)
			return
		}

		const { token } = await stripe.createToken(elements?.getElement(CardNumberElement)!)
		updatePrimaryCard(
			{ token: token?.id! },
			{
				onSuccess: () => {
					setIsSaving(false)
					onComplete()
				},
				onError: () => {
					Toast.show({
						type: 'error',
						text1: 'Failed to update, check your card and try again',
					})
					setIsSaving(false)
				},
			}
		)
	}

	return (
		<VStack gap={64}>
			<VStack gap={20}>
				<CardNumberElement
					onChange={onCardNumberChange}
					options={generateCardNumberStyleOptions({ windowWidth })}
				/>
				<HStack gap={20}>
					<CardExpiryElement
						onChange={onCardExpiryChange}
						options={generateCardMetadataOptions({ windowWidth })}
					/>
					<CardCvcElement
						onChange={onCardCvcChange}
						options={generateCardMetadataOptions({ windowWidth })}
					/>
				</HStack>
			</VStack>
			<View className="max-w-40">
				<Button isLoading={isSaving} onPress={onCardSave} disabled={!isCardComplete}>
					<ButtonText>Save</ButtonText>
				</Button>
			</View>
		</VStack>
	)
}

const BillingCardForm = ({ onComplete = () => {} }) => (
	<Elements stripe={loadStripe(Config.STRIPE_PUBLISHABLE_KEY)} options={ELEMENTS_OPTIONS}>
		<BillingCardFormInternal onComplete={onComplete} />
	</Elements>
)

export { BillingCardForm }
